import React, { useMemo, useRef, useState } from "react";
import useChartDimensions from "../SupportFiles/hooks/useChartDimensions";
import Tooltip from "../SupportFiles/Tooltip";
import PieInfo from "../SupportFiles/PieInfo";
import Select from "../SupportFiles/Select";
import config, { pieConfig, donutConfig } from "../SupportFiles/config";

const PieCharts = ({
  series = [],
  categories = [],
  options = {},
  height = "100%",
  width = "100%",
  donut = false,
  mode,
  circle,
}) => {
  const { ref, dims } = useChartDimensions({ w: 600, h: 300 });
  const padding = { ...config.padding, ...(options.padding || {}) };
  const baseInfo = {
    ...(pieConfig?.pie?.infoSection || {}),
    ...((donut ? donutConfig : pieConfig)?.pie?.infoSection || {}),
  };
  const innerW = Math.max(0, dims.w - padding.left - padding.right);
  const innerH = Math.max(0, dims.h - padding.top - padding.bottom);
  const showInfo =
    options.pie?.infoSection?.show ??
    options.pie?.showInfo ??
    baseInfo.show ??
    true;
  const infoWBase = Math.max(
    160,
    Math.min(
      280,
      options.pie?.infoSection?.width ??
        options.pie?.infoWidth ??
        baseInfo.width ??
        220
    )
  );
  const gapSide = 16;
  const sidePref =
    options.pie?.infoSection?.side ??
    options.pie?.infoPosition ??
    baseInfo.side ??
    "auto";
  const preferRight = sidePref === "left" ? false : true;
  const pieW = showInfo ? Math.max(80, innerW - (infoWBase + gapSide)) : innerW;
  const pieLeft = showInfo
    ? preferRight
      ? padding.left
      : padding.left + infoWBase + gapSide
    : padding.left;
  const cx = showInfo ? pieLeft + pieW / 2 : Math.floor(dims.w / 2);
  const cy = padding.top + innerH / 2;
  const useDonut = mode ? mode === "donut" : donut;
  const circleType =
    circle ??
    options.pie?.circle ??
    (useDonut ? donutConfig?.pie?.circle : pieConfig?.pie?.circle) ??
    "full";
  const isHalf = circleType === "half";
  const R = Math.max(
    1,
    Math.floor(Math.min(pieW, isHalf ? innerH * 2 : innerH) / 2 - 2)
  );
  const donutRatio = useDonut
    ? options.pie?.donutRatio ?? donutConfig?.pie?.donutRatio ?? 0.6
    : options.pie?.donutRatio ?? pieConfig?.pie?.donutRatio ?? 0;
  const r = Math.max(
    0,
    Math.floor(R * Math.min(0.95, Math.max(0, donutRatio)))
  );
  const values = useMemo(() => {
    if (Array.isArray(series) && typeof series[0] === "number") return series;
    const d =
      Array.isArray(series) && Array.isArray(series[0]?.data)
        ? series[0].data
        : [];
    return d;
  }, [series]);
  const labels =
    Array.isArray(categories) && categories.length === values.length
      ? categories
      : values.map((_, i) => `Slice ${i + 1}`);
  const [selected, setSelected] = useState([]);
  const allIndices = useMemo(() => values.map((_, i) => i), [values]);
  const activeIndices = selected && selected.length > 0 ? selected : allIndices;
  const valuesF = activeIndices.map((i) => values[i] ?? 0);
  const labelsF = activeIndices.map((i) => labels[i]);
  const total = valuesF.reduce(
    (a, v) => a + (typeof v === "number" ? Math.max(0, v) : 0),
    0
  );
  const colors = options.colors || config.colors;
  const valueDisplay =
    options.pie?.infoSection?.valueDisplay ?? baseInfo.valueDisplay ?? "value";
  const rawPieLabelCfg =
    options.pie?.showLabels ?? options.showLabels ?? config.showLabels;
  const pieLabelCfg =
    typeof rawPieLabelCfg === "object"
      ? rawPieLabelCfg
      : { show: !!rawPieLabelCfg };
  const showSliceLabels = pieLabelCfg.show ?? true;
  const resolveColorToken = (token) => {
    if (!token) return undefined;
    if (typeof token !== "string") return token;
    if (token.startsWith("#")) return token;
    const parts = token.split("/");
    const base = parts[0];
    const segs = base.split("-");
    const name = segs[0];
    const depth = segs[1];
    const varName = depth ? `--color-${name}-${depth}` : `--color-${name}`;
    if (typeof window !== "undefined") {
      const cssVal = getComputedStyle(document.documentElement)
        .getPropertyValue(varName)
        .trim();
      if (cssVal) return cssVal;
    }
    const tf = {
      "gray-50": "#f9fafb",
      "gray-100": "#f3f4f6",
      "gray-200": "#e5e7eb",
      "gray-300": "#d1d5db",
      "gray-400": "#9ca3af",
      "gray-500": "#6b7280",
      "gray-600": "#4b5563",
      "gray-700": "#374151",
      "gray-800": "#1f2937",
      "gray-900": "#111827",
      "primary-500": "#3b82f6",
      "info-500": "#06b6d4",
      "danger-500": "#ef4444",
      "success-500": "#10b981",
      "slate-400": "#94a3b8",
    };
    const key = depth ? `${name}-${depth}` : name;
    return tf[key] || token;
  };
  const sliceLabelColor = resolveColorToken(pieLabelCfg.color) ?? "#111827";
  const sliceLabelFontSize = pieLabelCfg.fontSize ?? 10;
  const sliceValueDisplay = pieLabelCfg.valueDisplay ?? valueDisplay;
  const sliceLabelStroke = pieLabelCfg.stroke;
  const sliceLabelStrokeWidth = pieLabelCfg.strokeWidth;
  const [tt, setTt] = useState({
    visible: false,
    x: 0,
    y: 0,
    title: "",
    subtitle: "",
  });
  const pathsRef = useRef([]);
  const buildArc = (cx, cy, R, r, start, end) => {
    const delta = end - start;
    const full = delta >= 2 * Math.PI - 1e-6;
    if (full) {
      if (r <= 0) {
        return `M ${cx + R} ${cy} A ${R} ${R} 0 1 1 ${
          cx - R
        } ${cy} A ${R} ${R} 0 1 1 ${cx + R} ${cy} Z`;
      }
      return `M ${cx + R} ${cy} A ${R} ${R} 0 1 1 ${
        cx - R
      } ${cy} A ${R} ${R} 0 1 1 ${cx + R} ${cy} L ${
        cx + r
      } ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${
        cx + r
      } ${cy} Z`;
    }
    const large = delta > Math.PI ? 1 : 0;
    const x1 = cx + R * Math.cos(start);
    const y1 = cy + R * Math.sin(start);
    const x2 = cx + R * Math.cos(end);
    const y2 = cy + R * Math.sin(end);
    if (r <= 0) {
      return `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
    }
    const xi1 = cx + r * Math.cos(end);
    const yi1 = cy + r * Math.sin(end);
    const xi2 = cx + r * Math.cos(start);
    const yi2 = cy + r * Math.sin(start);
    return `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${xi1} ${yi1} A ${r} ${r} 0 ${large} 0 ${xi2} ${yi2} Z`;
  };
  const totalAngle = circleType === "half" ? Math.PI : 2 * Math.PI;
  const orientation =
    options.pie?.orientation ??
    (useDonut ? donutConfig?.pie?.orientation : pieConfig?.pie?.orientation) ??
    "top";
  const gapDeg =
    options.pie?.gap ??
    (useDonut ? donutConfig?.pie?.gap : pieConfig?.pie?.gap) ??
    0;
  const gapRad = Math.max(0, (gapDeg * Math.PI) / 180);
  let startBase = 0;
  if (circleType === "half") {
    if (typeof options.pie?.startAngleDeg === "number") {
      startBase = (options.pie.startAngleDeg * Math.PI) / 180;
    } else {
      startBase =
        orientation === "top"
          ? 0
          : orientation === "right"
          ? -Math.PI / 2
          : orientation === "bottom"
          ? Math.PI
          : Math.PI / 2; // left
    }
  }
  let cxA = cx;
  let cyA = cy;
  if (circleType === "half") {
    const doShift = options.pie?.centerShift ?? true;
    if (doShift) {
      const shift = R / 2;
      if (orientation === "bottom") cyA = cy + shift;
      else if (orientation === "top") cyA = cy - shift;
      else if (orientation === "left") cxA = cx + shift;
      else if (orientation === "right") cxA = cx - shift;
    }
  }
  const slices = useMemo(() => {
    let acc = 0;
    return valuesF.map((v) => {
      const frac = total > 0 ? v / total : 0;
      const rawStart = startBase + acc * totalAngle;
      const rawEnd = startBase + (acc + frac) * totalAngle;
      const arcLen = Math.max(0, rawEnd - rawStart);
      const adjLen = Math.max(0, arcLen - gapRad);
      const start = rawStart + gapRad / 2;
      const end = start + adjLen;
      acc += frac;
      const mid = (start + end) / 2;
      const cxm = cxA + Math.cos(mid) * (r > 0 ? (R + r) / 2 : R * 0.66);
      const cym = cyA + Math.sin(mid) * (r > 0 ? (R + r) / 2 : R * 0.66);
      return { start, end, mid, cxm, cym };
    });
  }, [valuesF, total, cxA, cyA, R, r, totalAngle, startBase, gapRad]);
  return (
    <div ref={ref} style={{ width, height, position: "relative" }}>
      {options.legend?.mode === "select" && (
        <div
          style={{
            position: "absolute",
            right: padding.right + 8,
            top: padding.top + 8,
            zIndex: 2,
          }}
        >
          <Select
            options={labels.map((lab, i) => ({ label: lab, value: i }))}
            value={selected}
            onChange={setSelected}
            colors={Object.fromEntries(
              labels.map((_, i) => [i, colors[i % colors.length]])
            )}
            placeholder={
              options.legend?.searchPlaceholder ||
              options.pie?.infoSection?.searchPlaceholder ||
              options.pie?.searchPlaceholder ||
              "Search"
            }
            maxHeight={options.legend?.maxHeight ?? 160}
          />
        </div>
      )}
      <svg width={dims.w} height={dims.h}>
        <rect
          x="0"
          y="0"
          width={dims.w}
          height={dims.h}
          fill={options.background || config.background}
        />
        {valuesF.map((v, i) => {
          const idx = activeIndices[i];
          const color = colors[idx % colors.length];
          const arc = slices[i];
          const d = buildArc(cxA, cyA, R, r, arc.start, arc.end);
          return (
            <path
              key={`pie-${i}`}
              d={d}
              fill={color}
              onMouseEnter={() => {
                const estWidth = 160;
                let left = arc.cxm + 10;
                let top = arc.cym - 24;
                if (left + estWidth > dims.w - padding.right)
                  left = arc.cxm - estWidth - 10;
                if (top < padding.top) top = arc.cym + 12;
                setTt({
                  visible: true,
                  x: left,
                  y: top,
                  title: labelsF[i],
                  subtitle: String(v),
                });
              }}
              onMouseLeave={() => setTt((t) => ({ ...t, visible: false }))}
              ref={(el) => (pathsRef.current[i] = el)}
            />
          );
        })}
        {showSliceLabels &&
          valuesF.map((v, i) => {
            const arc = slices[i];
            const angleLen = Math.max(0, arc.end - arc.start);
            const minAngle = (6 * Math.PI) / 180;
            if (angleLen < minAngle) return null;
            const percent = total > 0 ? (v / total) * 100 : 0;
            const text =
              sliceValueDisplay === "percentage"
                ? `${Math.round(percent)}%`
                : sliceValueDisplay === "value_percentage"
                ? `${String(v)} / ${Math.round(percent)}%`
                : String(v);
            const labelR = r > 0 ? r + (R - r) * 0.5 : R * 0.66;
            const x = cxA + Math.cos(arc.mid) * labelR;
            const y = cyA + Math.sin(arc.mid) * labelR;
            return (
              <text
                key={`label-${i}`}
                x={x}
                y={y}
                fontSize={sliceLabelFontSize}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={sliceLabelColor}
                stroke={sliceLabelStroke}
                strokeWidth={sliceLabelStrokeWidth}
              >
                {text}
              </text>
            );
          })}
      </svg>
      <Tooltip
        visible={tt.visible}
        x={tt.x}
        y={tt.y}
        title={tt.title}
        subtitle={tt.subtitle}
      />
      {(() => {
        if (!showInfo) return null;
        const infoW = Math.max(
          160,
          Math.min(
            280,
            options.pie?.infoSection?.width ?? options.pie?.infoWidth ?? 220
          )
        );
        const gap = 16;
        const sidePref =
          options.pie?.infoSection?.side ?? options.pie?.infoPosition ?? "auto";
        const preferRight = sidePref === "left" ? false : true;
        const rightLeft = cxA + R + gap;
        const leftLeft = cxA - R - infoW - gap;
        let left;
        if (sidePref === "right") {
          left = dims.w - padding.right - infoW;
        } else if (sidePref === "left") {
          left = padding.left;
        } else {
          left =
            preferRight && rightLeft + infoW <= dims.w - padding.right
              ? rightLeft
              : leftLeft >= padding.left
              ? leftLeft
              : Math.max(padding.left + 8, dims.w - padding.right - infoW);
        }
        const leftOverride = options.pie?.infoSection?.left;
        if (typeof leftOverride === "number") left = leftOverride;
        const maxH = Math.max(
          120,
          Math.min(
            dims.h - padding.bottom - 16,
            options.pie?.infoSection?.height ??
              options.pie?.infoHeight ??
              baseInfo.height ??
              150
          )
        );
        const topOverride = options.pie?.infoSection?.top;
        const align =
          options.pie?.infoSection?.align ?? baseInfo.align ?? "center";
        let topVal;
        if (typeof topOverride === "number") {
          topVal = topOverride;
        } else {
          if (align === "center") {
            topVal = padding.top + Math.max(8, Math.floor((innerH - maxH) / 2));
          } else if (align === "bottom") {
            topVal = Math.max(padding.top + 8, dims.h - padding.bottom - maxH);
          } else {
            topVal = padding.top + 8;
          }
        }
        return (
          <PieInfo
            top={topVal}
            left={left}
            width={infoW}
            maxHeight={maxH}
            labels={labelsF}
            values={valuesF}
            percents={valuesF.map((v) => (total > 0 ? (v / total) * 100 : 0))}
            colors={activeIndices.map((i) => colors[i % colors.length])}
            total={total}
            placeholder={
              (options.pie?.infoSection?.searchPlaceholder ??
                options.pie?.searchPlaceholder) ||
              "Search"
            }
            showSearch={options.pie?.infoSection?.search ?? true}
            showTotal={
              typeof options.pie?.infoSection?.total === "boolean"
                ? options.pie?.infoSection?.total
                : baseInfo.total ?? true
            }
            totalLabel={
              options.pie?.infoSection?.totalLabel ?? baseInfo.totalLabel ?? "Total"
            }
            valueDisplay={valueDisplay}
          />
        );
      })()}
    </div>
  );
};

export default PieCharts;
